/**
 * Shared extraction of user-facing messages from failed API calls.
 *
 * Two backends answer this app and they do not share an error shape:
 * - `hv-serviceman-api` handles auth and user settings itself and returns `{ error: string }`.
 * - `hv-maintenance` answers everything else and returns `{ type, message, code, fields }`.
 *   The serviceman-api proxy forwards that body verbatim, so `code` survives the hop.
 *
 * Both backends phrase `message` / `error` in English technical wording, so neither is ever
 * shown to the user. Instead the stable `code` is mapped to a translated key, with HTTP
 * status as the fallback signal.
 *
 * @module utils/apiError
 */
import axios from 'axios'
import type { TFunction } from 'i18next'

/**
 * Maps `hv-maintenance` error codes to translation keys.
 *
 * Codes are a wire contract defined in `hv-maintenance/error_codes.go`; the E310xxx range
 * covers the maintenance-request create flow. Unmapped codes fall through to the HTTP
 * status, so the frontend stays safe to deploy ahead of the backend.
 */
export const API_ERROR_CODE_KEYS: Readonly<Record<string, string>> = {
  E310001: 'errors.api.maintenanceInvalidPayload',
  E310002: 'errors.api.maintenanceCompanyNotFound',
  E310003: 'errors.api.maintenanceDuplicateContact',
  E310004: 'errors.api.maintenanceInsertFailed',
  E310005: 'errors.api.maintenanceContactNotFound',
}

/** Error body shape returned by `hv-maintenance` (and proxied verbatim by serviceman-api). */
interface MaintenanceErrorBody {
  type?: string
  message?: string
  code?: string
  fields?: string[]
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

/**
 * Resolve a failed request to a translation key, without performing the translation.
 *
 * Kept free of i18n so it can be unit tested in isolation and reused wherever a raw key
 * is more useful than a rendered string.
 *
 * Resolution order: backend error code, then HTTP status, then the caller's fallback.
 *
 * @param error - The value caught in a `catch` block (typically an `AxiosError`).
 * @param fallbackKey - Translation key used when nothing more specific applies.
 * @returns A translation key that is guaranteed to exist in the locale files.
 *
 * @example
 * getApiErrorKey(axiosError, 'maintenance.createFailed') // 'errors.api.maintenanceDuplicateContact'
 * getApiErrorKey(new Error('boom'), 'maintenance.createFailed') // 'maintenance.createFailed'
 */
export const getApiErrorKey = (error: unknown, fallbackKey: string): string => {
  if (!axios.isAxiosError(error)) return fallbackKey

  // No response at all means the request never completed (offline, DNS, timeout, CORS).
  if (!error.response) return 'errors.networkError'

  const data: MaintenanceErrorBody | undefined = isRecord(error.response.data) ? (error.response.data as MaintenanceErrorBody) : undefined

  if (typeof data?.code === 'string') {
    const mappedKey = API_ERROR_CODE_KEYS[data.code]
    if (mappedKey) return mappedKey
  }

  const { status } = error.response
  if (status === 401) return 'errors.unauthorized'
  if (status === 403) return 'errors.forbidden'
  if (status === 404) return 'errors.notFound'
  if (status >= 500) return 'errors.serverError'

  return fallbackKey
}

/**
 * Resolve a failed request to a translated, user-facing message.
 *
 * Falls back to the i18n singleton when no `t` is supplied, so Redux thunks and plain
 * hooks can use this without access to React context.
 *
 * @param error - The value caught in a `catch` block (typically an `AxiosError`).
 * @param fallbackKey - Translation key used when nothing more specific applies.
 * @param t - Optional translator from `useTranslation()`; defaults to the i18n singleton.
 * @returns The translated message ready to show in a notification or inline error.
 *
 * @example
 * showNotification(getApiErrorMessage(error, 'maintenance.createFailed', t), 'error')
 */
export const getApiErrorMessage = (error: unknown, fallbackKey: string, t?: TFunction): string => {
  const key = getApiErrorKey(error, fallbackKey)
  if (t) return t(key) as string

  // Loaded lazily so importing this module never boots i18n for callers that pass their own `t`.
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  const i18n = require('../i18n').default
  return i18n.t(key) as string
}
